import type {
  AssessmentAnswerItem,
  AssessmentAnswerPayload,
  AssessmentQuestionAnswerBlock,
} from "../axios/quiz";
import type { InteractionSubmitPayload } from "../axios/interaction";
import type { AssessmentSendAnswerOptions } from "./assessmentInteractive";

export type AssessmentDraftFromPrevious = {
  answer: AssessmentAnswerItem;
  options?: AssessmentSendAnswerOptions;
  timeSpent: number;
  finalized?: boolean;
};

const isQuestionBlock = (
  entry: AssessmentAnswerItem | AssessmentQuestionAnswerBlock,
): entry is AssessmentQuestionAnswerBlock =>
  typeof (entry as AssessmentQuestionAnswerBlock).type === "string";

const isInteractivePayload = (
  value: unknown,
): value is InteractionSubmitPayload => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    ("user_answers" in record && typeof record.user_answers === "object") ||
    ("user_order" in record && Array.isArray(record.user_order)) ||
    ("user_pairs" in record && Array.isArray(record.user_pairs))
  );
};

/**
 * Maps socket `previous_answers` (raw `/assessment_answer` payloads) into
 * in-memory draft answers for edit-mode resume.
 */
export const mapPreviousAnswersToDrafts = (
  previousAnswers: AssessmentAnswerPayload[] | null | undefined,
): Record<number, AssessmentDraftFromPrevious> => {
  if (!Array.isArray(previousAnswers) || previousAnswers.length === 0) {
    return {};
  }

  const drafts: Record<number, AssessmentDraftFromPrevious> = {};

  for (const payload of previousAnswers) {
    if (!payload || typeof payload !== "object") continue;

    const first = Array.isArray(payload.answers) ? payload.answers[0] : null;
    if (!first || typeof first !== "object") continue;
    if (typeof first.question_index !== "number") continue;

    const questionIndex = first.question_index;
    const chosenAnswer =
      typeof first.chosen_answer === "string" ? first.chosen_answer : "";

    const answerItem = first as Partial<AssessmentAnswerItem>;
    const answer: AssessmentAnswerItem = {
      question_index: questionIndex,
      chosen_answer: chosenAnswer,
      concept: typeof answerItem.concept === "string" ? answerItem.concept : "",
      score:
        typeof answerItem.score === "number" || answerItem.score === null
          ? answerItem.score
          : null,
      all_score:
        typeof answerItem.all_score === "number" ? answerItem.all_score : 0,
    };

    const options: AssessmentSendAnswerOptions = {};
    if (isInteractivePayload(payload.payload)) {
      options.interactivePayload = payload.payload;
    }
    if (isQuestionBlock(first)) {
      options.questionBlock = first;
    }

    drafts[questionIndex] = {
      answer,
      options:
        options.interactivePayload || options.questionBlock
          ? options
          : undefined,
      timeSpent: 0,
      ...(payload.is_submitted === true ? { finalized: true } : {}),
    };
  }

  return drafts;
};
